/**
 * Геометрия сот: H3 res 10 → GeoJSON для MapLibre.
 *
 * Назначение файла: превратить ответ GET /map/hexes в слой карты. Состояние сот
 * приходит с сервера; здесь только геометрия и раскраска по ownerKind.
 */

import type { Feature, FeatureCollection, Polygon } from "geojson";
import type { MapHexListItem } from "@hive/shared/api/map";
import { cellToBoundary, cellToLatLng, getResolution, gridDisk, latLngToCell } from "h3-js";

/** Разрешение H3 из ТЗ: сота размером двора. */
export const HEX_RESOLUTION = 10;

/** Ниже этого масштаба соты не рисуются. */
export const MIN_HEX_ZOOM = 14;

export function hexAt(lat: number, lon: number): string {
  return latLngToCell(lat, lon, HEX_RESOLUTION);
}

export function hexCenter(h3: string): { lat: number; lon: number } {
  const [lat, lon] = cellToLatLng(h3);
  return { lat, lon };
}

export function isGameHex(h3: string): boolean {
  try {
    return getResolution(h3) === HEX_RESOLUTION;
  } catch {
    return false;
  }
}

export function distanceMeters(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
): number {
  const earthRadius = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(a));
}

export interface HexFeatureProperties {
  readonly h3: string;
  readonly kind: MapHexListItem["type"];
  readonly fog: MapHexListItem["fog"];
  readonly paint: "you" | "clan" | "ally" | "enemy" | "neutral" | "free" | "sanctuary";
  readonly vaultFill: number;
  readonly isHearth: boolean;
  readonly isHearthRing: boolean;
}

function paintOf(hex: MapHexListItem, hearthH3: string | null): HexFeatureProperties["paint"] {
  if (hearthH3 && hex.hexId === hearthH3) return "you";
  if (hex.type === "sanctuary") return "sanctuary";
  switch (hex.ownerKind) {
    case "none":
      return "free";
    case "clan":
      return "clan";
    case "player":
      return "enemy";
    default:
      return "neutral";
  }
}

export function hexesToGeoJson(
  hexes: readonly MapHexListItem[],
  hearthH3: string | null,
): FeatureCollection<Polygon, HexFeatureProperties> {
  const hearthRing = hearthH3 ? new Set(gridDisk(hearthH3, 1)) : new Set<string>();

  const features = hexes.map((hex) => {
    const h3 = hex.hexId;
    const isHearth = h3 === hearthH3;
    const boundary = cellToBoundary(h3).map(([lat, lon]) => [lon, lat] as [number, number]);
    const first = boundary[0];
    if (first) boundary.push(first);

    const owned = hex.ownerKind !== "none";
    const explored = hex.fog === "visible";
    const properties: HexFeatureProperties = {
      h3,
      kind: hex.type,
      fog: hex.fog,
      paint: paintOf(hex, hearthH3),
      vaultFill: explored && owned ? 0.45 : explored ? 0.18 : 0,
      isHearth,
      isHearthRing: !isHearth && hearthRing.has(h3),
    };

    const feature: Feature<Polygon, HexFeatureProperties> = {
      type: "Feature",
      id: h3,
      geometry: { type: "Polygon", coordinates: [boundary] },
      properties,
    };
    return feature;
  });

  return { type: "FeatureCollection", features };
}
